import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList, faPlayCircle, faStar, faUsers, faUserSlash, faChalkboardUser } from '@fortawesome/free-solid-svg-icons';
import './DemoReport.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

// SubmitDemoClasses.ClassDateTime is saved as Lebanon wall-clock digits: read it as UTC
const when = (v) =>
  v ? new Date(v).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }) : '';

const Stars = ({ n }) => (
  <span className="rmdr-stars" aria-label={`${n} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <FontAwesomeIcon key={i} icon={faStar} className={i <= n ? 'is-on' : ''} />
    ))}
  </span>
);

/* One report: what the teacher wrote in the demo form */
export const DemoReportBody = ({ r }) => (
  <div className="rmdr-report">
    <dl className="rmdr-facts">
      <div>
        <dt>Class</dt>
        <dd>
          {when(r.at)}
          {r.teacher ? ` · ${r.teacher}` : ''}
        </dd>
      </div>
      <div>
        <dt>How it went</dt>
        <dd className={r.status === 0 ? 'is-good' : 'is-bad'}>{r.statusLabel}</dd>
      </div>
      {r.project && (
        <div>
          <dt>Project</dt>
          <dd>
            {r.project.link ? (
              <a href={r.project.link} target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faPlayCircle} /> {r.project.name}
              </a>
            ) : (
              r.project.name
            )}
          </dd>
        </div>
      )}
      {r.parents != null && (
        <div>
          <dt>Parents</dt>
          <dd>
            <FontAwesomeIcon icon={r.parents ? faUsers : faUserSlash} /> {r.parents ? 'Were there' : 'Not there'}
          </dd>
        </div>
      )}
      {r.rating != null && (
        <div>
          <dt>Teacher’s rating</dt>
          <dd>
            <Stars n={r.rating} />
          </dd>
        </div>
      )}
      {r.result && (
        <div>
          <dt>Result</dt>
          <dd>{r.result}</dd>
        </div>
      )}
    </dl>
    <div className="rmdr-notes">
      <span>Teacher notes</span>
      {r.notes ? <p>{r.notes}</p> : <p className="is-empty">No notes.</p>}
    </div>
  </div>
);

/* The teacher's demo report.
   studentId: on a student profile (a paid student shows the demo they came from; nothing if there is none)
   classId:   one demo class (the Classes page) */
const DemoReport = ({ studentId, classId, card = true }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    const url = classId ? `${API}/api/admin/studentprofile/demoreport/class/${classId}` : `${API}/api/admin/studentprofile/demoreport/${studentId}`;
    axios
      .get(url, auth())
      .then((res) => alive && setData(res.data))
      .catch((err) => alive && setError(err.response?.data?.error || 'We couldn’t load the teacher’s report.'));
    return () => {
      alive = false;
    };
  }, [studentId, classId]);

  const reports = data?.reports || [];
  if (card && data && !reports.length) return null; // profile: no demo report, nothing to show

  const body = error ? (
    <p className="rmdr-error">{error}</p>
  ) : !data ? (
    <div className="rmdr-skel" aria-busy="true" />
  ) : !reports.length ? (
    <p className="rmdr-empty">The teacher hasn’t submitted the report yet.</p>
  ) : (
    reports.map((r, i) => <DemoReportBody key={`${r.at}-${i}`} r={r} />)
  );

  if (!card) return <div className="rmdr">{body}</div>;
  return (
    <section className="rmdr rmasp-card">
      <h2 className="rmasp-h2">
        <FontAwesomeIcon icon={data?.fromDemo ? faChalkboardUser : faClipboardList} /> {data?.fromDemo ? 'Their free class (demo report)' : 'Teacher’s demo report'}
      </h2>
      {body}
    </section>
  );
};

export default DemoReport;
